// src/components/ReservasFilterBar.tsx
import { useMemo, useEffect } from "react";
import { Autocomplete, TextField, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useReservas } from "../hooks/useReservas";
import type { ReservaFilters } from "../types/ReservaFilters";
import { useAuth } from "../context/AuthContext";
import { useProfesores } from "../hooks/useProfesores";
import { useClientes } from "../hooks/useClientes";
import { useAsignaturas } from "../hooks/useAsignaturas";
import { useCentros } from "../hooks/useCentros";
import { ESTADOS, CURSOS } from "../constants/enums";
import { useProyectos } from "../hooks/useProyectos";
import { useFormatos } from "../hooks/useFormatos";

/* --------------------------- tipos --------------------------- */
export interface Filtros {
  profesorId?: number;
  clienteIdUsr?: number;
  asignaturaId?: number;
  centroId?: number;
  idProyecto?: number;
  estado?: string;
  curso?: string;
  idFormato?: number;
}

/* util: normalizar truthy/falsey para 'activo' (clientes) */
const isActiveTrue = (v: any) => {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (["1", "true", "si", "sí", "activo", "active", "enabled", "yes"].includes(s)) return true;
    if (["0", "false", "no", "inactivo", "inactive", "baja", "archivado", "disabled"].includes(s))
      return false;
  }
  return !!v;
};

/* --------------------------- componente ---------------------- */
export default function FiltersBar({
  value,
  onChange,
  hideProfesor = false,
  hideCliente = false,
}: {
  value: Filtros;
  onChange: (next: Filtros) => void;
  hideProfesor?: boolean;
  hideCliente?: boolean;
}) {
  const { t } = useTranslation<"translation">();

  const { user } = useAuth(); // { id, roles, ... }
  const isProfesorLogueado = !!user?.roles?.includes("profesor");

  // ⬇️ Profesores solo activos desde el hook
  const { rows: profesores = [] } = useProfesores({ soloActivos: true });

  const { rows: clientesRaw = [] } = useClientes();
  const { rows: asignaturas = [] } = useAsignaturas();
  const { rows: centros = [] } = useCentros();
  const { rows: formatos = [] } = useFormatos();
  const { rows: proyectos = [] } = useProyectos();

  const set = (k: keyof Filtros, v: any) => onChange({ ...value, [k]: v });

  // ⬇️ Clientes activos (si el endpoint de clientes trae 'activo')
  const clientes = useMemo(
    () => clientesRaw.filter((c: any) => isActiveTrue(c?.activo ?? true)),
    [clientesRaw]
  );

  // === 1) Mapeo user.id -> profesores.id (en tu modelo coincide)
  const meProfesor = useMemo(
    () =>
      isProfesorLogueado
        ? profesores.find(
            (p: any) =>
              Number(p?.id) === Number(user?.id) ||
              p?.userId === user?.id ||
              p?.idUsuario === user?.id ||
              p?.idUsr === user?.id
          )
        : undefined,
    [isProfesorLogueado, profesores, user]
  );

  const profesorIdReal: number | undefined = meProfesor?.id;

  // === 2) Cargar histórico del profesor SOLO cuando tengamos profesorId
  const START_ALL = "2000-01-01";
  const END_ALL = "2100-01-01";

  const filtrosReservas: ReservaFilters =
    isProfesorLogueado && profesorIdReal ? { profesorId: profesorIdReal } : {};

  const { rows: reservasProfesor = [] } = useReservas(
    isProfesorLogueado && profesorIdReal ? START_ALL : null,
    isProfesorLogueado && profesorIdReal ? END_ALL : null,
    filtrosReservas
  );

  // === 3) Alumnos visibles:
  // Profesores -> solo los que aparezcan en sus reservas (idCliente y alumnosIds) y estén activos
  // Otros roles -> todos los clientes activos
  const clientesVisibles = useMemo(() => {
    if (!isProfesorLogueado) return clientes;
    if (!profesorIdReal) return [];

    const ids = new Set<number>();
    reservasProfesor.forEach((r: any) => {
      const titular = Number(r.idCliente);
      if (Number.isFinite(titular)) ids.add(titular);
      (r.alumnosIds ?? []).forEach((id: any) => {
        const n = Number(id);
        if (Number.isFinite(n)) ids.add(n);
      });
    });

    return clientes.filter((c: any) => ids.has(Number(c.id)));
  }, [isProfesorLogueado, profesorIdReal, reservasProfesor, clientes]);

  // === 4) Limpiar selecciones que ya no están disponibles
  useEffect(() => {
    const patches: Partial<Filtros> = {};

    const ensureIdIn = (key: keyof Filtros, opts: any[]) => {
      const val = value[key] as any;
      if (val == null) return;
      const ok = opts.some((o: any) => Number(o.id) === Number(val));
      if (!ok) patches[key] = undefined as any;
    };

    ensureIdIn("profesorId", profesores);
    ensureIdIn("clienteIdUsr", clientesVisibles);

    if (value.estado && !(ESTADOS as readonly string[]).includes(value.estado)) {
      patches.estado = undefined;
    }
    if (value.curso && !(CURSOS as readonly string[]).includes(value.curso)) {
      patches.curso = undefined;
    }

    if (Object.keys(patches).length) onChange({ ...value, ...patches });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profesores, clientesVisibles]);

  return (
    <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", mb: 2 }}>
      {/* Profesor (solo activos) */}
      {!hideProfesor && (
        <Autocomplete
          size="small"
          options={profesores}
          getOptionLabel={(o: any) =>
            o?.nombre ?? o?.nombreCompleto ?? String(o?.email ?? o?.id ?? "")
          }
          isOptionEqualToValue={(o: any, v: any) => Number(o.id) === Number(v.id)}
          value={profesores.find((p: any) => Number(p.id) === Number(value.profesorId)) ?? null}
          onChange={(_, v: any) => set("profesorId", v?.id)}
          renderInput={(p) => <TextField {...p} label={t("filters.profesor")} />}
          sx={{ width: 210 }}
        />
      )}

      {/* Cliente / Alumno (solo activos) */}
      {!hideCliente && (
        <Autocomplete
          size="small"
          options={clientesVisibles}
          getOptionLabel={(o: any) => o?.nombreCompleto ?? o?.nombre ?? String(o?.id ?? "")}
          isOptionEqualToValue={(o: any, v: any) => Number(o.id) === Number(v.id)}
          value={
            clientesVisibles.find((c: any) => Number(c.id) === Number(value.clienteIdUsr)) ?? null
          }
          onChange={(_, v: any) => set("clienteIdUsr", v?.id)}
          renderInput={(p) => <TextField {...p} label={t("filters.cliente")} />}
          sx={{ width: 210 }}
        />
      )}

      {/* Asignatura */}
      <Autocomplete
        size="small"
        options={asignaturas}
        getOptionLabel={(o: any) => o?.nombre ?? String(o?.id ?? "")}
        isOptionEqualToValue={(o: any, v: any) => Number(o.id) === Number(v.id)}
        value={asignaturas.find((a: any) => Number(a.id) === Number(value.asignaturaId)) ?? null}
        onChange={(_, v: any) => set("asignaturaId", v?.id)}
        renderInput={(p) => <TextField {...p} label={t("filters.asignatura")} />}
        sx={{ width: 210 }}
      />

      {/* Formato */}
      <Autocomplete
        size="small"
        options={formatos}
        getOptionLabel={(o: any) => o?.nombreFormato ?? String(o?.id ?? "")}
        isOptionEqualToValue={(o: any, v: any) => Number(o.id) === Number(v.id)}
        value={formatos.find((f: any) => Number(f.id) === Number(value.idFormato)) ?? null}
        onChange={(_, v: any) => set("idFormato", v?.id)}
        renderInput={(p) => <TextField {...p} label={t("filters.formato")} />}
        sx={{ width: 210 }}
      />

      {/* Centro */}
      <Autocomplete
        size="small"
        options={centros}
        getOptionLabel={(o: any) => o?.nombre ?? String(o?.id ?? "")}
        isOptionEqualToValue={(o: any, v: any) => Number(o.id) === Number(v.id)}
        value={centros.find((c: any) => Number(c.id) === Number(value.centroId)) ?? null}
        onChange={(_, v: any) => set("centroId", v?.id)}
        renderInput={(p) => <TextField {...p} label={t("filters.centro")} />}
        sx={{ width: 210 }}
      />

      {/* Curso */}
      <Autocomplete
        size="small"
        options={CURSOS as readonly string[]}
        value={value.curso ?? null}
        onChange={(_, v) => set("curso", v ?? undefined)}
        renderInput={(p) => <TextField {...p} label={t("filters.curso")} />}
        sx={{ width: 340 }}
        disableCloseOnSelect
      />

      {/* Proyecto */}
      <Autocomplete
        size="small"
        options={proyectos}
        getOptionLabel={(o: any) => o?.nombre ?? String(o?.id ?? "")}
        isOptionEqualToValue={(o: any, v: any) => Number(o.id) === Number(v.id)}
        value={proyectos.find((p: any) => Number(p.id) === Number(value.idProyecto)) ?? null}
        onChange={(_, v: any) => set("idProyecto", v?.id)}
        renderInput={(p) => <TextField {...p} label={t("filters.proyecto")} />}
        sx={{ width: 240 }}
      />

      {/* Estado */}
      <Autocomplete
        size="small"
        options={ESTADOS as readonly string[]}
        value={value.estado ?? null}
        onChange={(_, v) => set("estado", v ?? undefined)}
        renderInput={(p) => <TextField {...p} label={t("filters.estado")} />}
        sx={{ width: 20 }}
      />
    </Stack>
  );
}
